// Read-only demo counterpart to VideoViewer. Reads the video blob from
// IndexedDB (written by DemoVideoUpload) instead of calling Cloudflare Stream.
import '../../components/common/VideoViewer/VideoViewer.css';
import 'plyr/dist/plyr.css';

import React, { useEffect, useRef, useState } from 'react';

// plyr 3.x ships a .d.ts that mixes `export =` with `export default`, which
// trips up TypeScript's default-import inference even with esModuleInterop.
// The runtime ESM build (dist/plyr.mjs) exports Plyr as the default, so the
// import is correct at runtime and the class type resolves for usage below.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error -- see comment above
import Plyr from 'plyr';

import SpinnerIcon from '../../components/common/SpinnerIcon';
import { getVideoBlob } from '../store/videoBlobStore';

export interface DemoVideoViewerProps {
  videoUid: string;
  label?: string;
}

const DemoVideoViewer: React.FC<DemoVideoViewerProps> = ({ videoUid, label = 'Athlete video' }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [aspect, setAspect] = useState<{ width: number; height: number } | null>(null);
  const srcRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const plyrRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const blob = await getVideoBlob(videoUid);
        if (cancelled) return;
        if (!blob) { setError(true); return; }
        const url = URL.createObjectURL(blob);
        srcRef.current = url;
        setSrc(url);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (srcRef.current) {
        URL.revokeObjectURL(srcRef.current);
        srcRef.current = null;
      }
    };
  }, [videoUid]);

  useEffect(() => {
    if (!src || !videoRef.current) return;
    if (plyrRef.current) plyrRef.current.destroy();
    plyrRef.current = new Plyr(videoRef.current, {
      controls: ['play', 'play-large', 'progress', 'current-time', 'duration', 'settings', 'mute'],
      autoplay: false,
      autopause: false,
      clickToPlay: true,
      muted: true,
      speed: { selected: 1, options: [0.5, 1, 1.5, 2] },
      settings: ['speed', 'loop'],
      fullscreen: { enabled: false, iosNative: false },
      keyboard: { focused: false, global: false },
      loop: { active: true },
      hideControls: false,
    });
    return () => {
      if (plyrRef.current) {
        plyrRef.current.destroy();
        plyrRef.current = null;
      }
    };
  }, [src]);

  const aspectStyle: React.CSSProperties = aspect
    ? { aspectRatio: `${aspect.width} / ${aspect.height}` }
    : { aspectRatio: '9 / 16' };

  if (error) return <div className="video-viewer-error">Video unavailable</div>;

  if (!src) {
    return (
      <div className="video-viewer-skeleton" style={aspectStyle}>
        <SpinnerIcon className="video-viewer-spinner" />
      </div>
    );
  }

  return (
    <div className="video-viewer-player" style={aspectStyle}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={src}
        playsInline
        muted
        preload="metadata"
        title={label}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (v.videoWidth && v.videoHeight) {
            setAspect({ width: v.videoWidth, height: v.videoHeight });
          }
        }}
      />
    </div>
  );
};

export default DemoVideoViewer;
